/**
 * Manufacturer Cart Renderer
 * Handles rendering of cart items grouped by manufacturer with progress bars
 */

class ManufacturerCartRenderer {
  constructor() {
    this.cart = null;
    this.manufacturerCart = new ManufacturerCart();
    this.container = null;
    this.previousState = null; // Store previous state for incremental updates
  }

  async init() {
    this.container = document.getElementById("manufacturer-cart-container");
    if (!this.container) return;

    // Use cart data from window if available (includes metafields), otherwise fetch
    if (window.cartItemsData && window.cartItemsData.length > 0) {
      this.cart = { items: window.cartItemsData };
    } else {
      await this.manufacturerCart.fetchCart();
      this.cart = this.manufacturerCart.cart;
    }

    if (!this.cart || !this.cart.items || this.cart.items.length === 0) {
      this.container.innerHTML = `<div class="cart-empty">${this.t("cart_empty")}</div>`;
      return;
    }

    // Render cart
    await this.render();
  }

  async render() {
    if (!this.container) return;

    // IMPORTANT: Add index property to each item based on its position in cart.items array
    // Shopify cart.js doesn't include index, but we need it for line numbers (1-based)
    this.cart.items.forEach((item, index) => {
      item.index = index; // 0-based index
      item.line = index + 1; // 1-based line number for Shopify API
    });

    // Calculate progress for all manufacturers
    const progress = await this.manufacturerCart.calculateProgress(
      this.cart.items,
    );

    // Group items by manufacturer
    console.log(this.cart.items);
    const { groups, noManufacturer } =
      this.manufacturerCart.groupByManufacturer(this.cart.items);

    // Try incremental update first if we have previous state and container has content
    if (
      this.previousState &&
      this.container &&
      this.container.children.length > 0
    ) {
      // Check if container actually has rendered content (not just loading message)
      const hasRenderedContent =
        this.container.querySelector(".manufacturer-group") !== null;

      if (hasRenderedContent) {
        const updated = await this.updateIncremental(
          progress,
          groups,
          noManufacturer,
        );
        if (updated) {
          // Incremental update succeeded
          await this.updateCheckoutState(progress);
          // Store new state for next update
          this.previousState = {
            cart: JSON.parse(JSON.stringify(this.cart)),
            progress: JSON.parse(JSON.stringify(progress)),
            groups: new Map(groups),
            noManufacturer: [...noManufacturer],
          };
          // Re-enable controls after successful update
          this.setQuantityControlsEnabled(true);
          return;
        }
      }
      // If incremental update failed or no rendered content, fall through to full render
    }

    // Full render (first time or if incremental update failed)
    // Use DocumentFragment to minimize reflow/repaint
    const fragment = document.createDocumentFragment();
    const tempDiv = document.createElement("div");

    let html = "";

    // Render manufacturer groups
    for (const [manufacturerId, group] of groups) {
      const manufacturer = group.manufacturer;
      const items = group.items;
      const manufacturerProgress = progress[manufacturerId];

      if (!manufacturerProgress) continue;

      const nombre =
        manufacturer.nombre?.value ||
        manufacturer.nombre ||
        this.t("unknown_manufacturer");
      const foto = manufacturer.foto?.value;
      const urlFabricante =
        manufacturer.url_fabricante?.value || manufacturer.url_fabricante;
      const progressData = manufacturerProgress.progress;
      console.log(JSON.stringify(manufacturer, null, 2));
      html += this.renderManufacturerGroup(
        manufacturerId,
        nombre,
        foto,
        urlFabricante,
        items,
        progressData,
      );
    }

    // Render items without manufacturer
    if (noManufacturer.length > 0) {
      html += this.renderNoManufacturerGroup(noManufacturer);
    }

    // Build HTML in temporary container to minimize DOM manipulation
    tempDiv.innerHTML = html;

    // Clear container and append new content in one operation
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }

    // Move all nodes from tempDiv to container
    while (tempDiv.firstChild) {
      this.container.appendChild(tempDiv.firstChild);
    }

    // Wire up quantity buttons and remove buttons
    this.setupCartInteractions();

    // Update checkout button state
    await this.updateCheckoutState(progress);

    // Re-enable controls after render completes
    this.setQuantityControlsEnabled(true);

    // Store state for next incremental update
    this.previousState = {
      cart: JSON.parse(JSON.stringify(this.cart)),
      progress: JSON.parse(JSON.stringify(progress)),
      groups: new Map(groups),
      noManufacturer: [...noManufacturer],
    };
  }

  async updateIncremental(newProgress, newGroups, newNoManufacturer) {
    try {
      // Compare old and new cart to find what actually changed
      const oldCart = this.previousState?.cart;
      const changedLineNumbers = new Set();

      if (oldCart && oldCart.items) {
        // Find items that changed (quantity, price, or were added/removed)
        const oldItemsMap = new Map(
          oldCart.items.map((item) => [
            item.line || (item.index !== undefined ? item.index + 1 : 1),
            item,
          ]),
        );
        const newItemsMap = new Map(
          this.cart.items.map((item) => [
            item.line || (item.index !== undefined ? item.index + 1 : 1),
            item,
          ]),
        );

        // Check for changed quantities or prices
        for (const [lineNumber, newItem] of newItemsMap) {
          const oldItem = oldItemsMap.get(lineNumber);
          if (
            !oldItem ||
            oldItem.quantity !== newItem.quantity ||
            (oldItem.final_line_price || oldItem.line_price) !==
              (newItem.final_line_price || newItem.line_price)
          ) {
            changedLineNumbers.add(lineNumber);
          }
        }

        // Check for removed items
        for (const [lineNumber] of oldItemsMap) {
          if (!newItemsMap.has(lineNumber)) {
            changedLineNumbers.add(lineNumber);
          }
        }
      }

      // Update progress bars for each manufacturer group
      for (const [manufacturerId, group] of newGroups) {
        const manufacturerGroup = this.container.querySelector(
          `[data-manufacturer-id="${manufacturerId}"]`,
        );
        if (!manufacturerGroup) {
          // Manufacturer group doesn't exist, need full render
          return false;
        }

        const manufacturerProgress = newProgress[manufacturerId];
        if (!manufacturerProgress) continue;

        const progressData = manufacturerProgress.progress;

        // Get old progress data to compare
        const oldProgress = this.previousState?.progress?.[manufacturerId];
        const oldProgressData = oldProgress?.progress;
        let progressChanged = false;

        // Check if progress actually changed
        if (oldProgressData) {
          if (
            progressData.type === 1 &&
            progressData.containers &&
            oldProgressData.containers
          ) {
            // Compare container percentages, weight, and validity
            for (let i = 0; i < progressData.containers.length; i++) {
              const oldContainer = oldProgressData.containers[i];
              const newContainer = progressData.containers[i];
              if (
                !oldContainer ||
                Math.round(oldContainer.percentage || 0) !==
                  Math.round(newContainer.percentage || 0) ||
                oldContainer.valid !== newContainer.valid ||
                Math.round(oldContainer.totalWeight || 0) !==
                  Math.round(newContainer.totalWeight || 0) ||
                Math.round(oldContainer.weightPercent || 0) !==
                  Math.round(newContainer.weightPercent || 0) ||
                oldContainer.isInvalidContainer !==
                  newContainer.isInvalidContainer
              ) {
                progressChanged = true;
                break;
              }
            }
          } else if (
            Math.round(oldProgressData.percentage || 0) !==
              Math.round(progressData.percentage || 0) ||
            oldProgressData.valid !== progressData.valid
          ) {
            progressChanged = true;
          }
        } else {
          progressChanged = true; // No old data, assume changed
        }

        // Only update progress bars if they actually changed
        if (progressChanged || changedLineNumbers.size > 0) {
          // Update progress bars
          if (
            progressData.type === 1 &&
            progressData.containers &&
            Array.isArray(progressData.containers)
          ) {
            // Type 1: Update each container
            for (const container of progressData.containers) {
              const containerEl = manufacturerGroup.querySelector(
                `[data-container-index="${container.containerIndex}"]`,
              );
              if (!containerEl) {
                // Container doesn't exist, need full render
                return false;
              }

              const percentage = Math.round(container.percentage || 0);
              const isValid = container.valid || false;
              const isComplete = isValid && percentage >= 100;

              if (isComplete) {
                // Check if it should be minimized
                if (!containerEl.classList.contains("container-complete")) {
                  // Need to switch to minimized view - do full render
                  return false;
                }
              } else {
                // Update progress bar width smoothly
                const progressBarFill =
                  containerEl.querySelector(".progress-bar-fill");
                if (progressBarFill) {
                  const currentWidth =
                    parseFloat(progressBarFill.style.width) || 0;
                  const newWidth = percentage;

                  // Only update if width actually changed
                  if (Math.abs(currentWidth - newWidth) > 0.5) {
                    // Progress bar color based on validity, not weight warning
                    // Weight warning is informational only and doesn't affect bar color
                    const progressClass = isValid
                      ? "complete"
                      : percentage >= 50
                        ? "warning"
                        : "error";
                    progressBarFill.className = `progress-bar-fill ${progressClass}`;
                    // Use requestAnimationFrame for smooth transition
                    requestAnimationFrame(() => {
                      progressBarFill.style.width = `${percentage}%`;
                    });
                  }
                }

                // Update percentage text only if changed
                const percentageEl = containerEl.querySelector(
                  ".progress-percentage",
                );
                if (percentageEl) {
                  const currentText = percentageEl.textContent.trim();
                  const newText = `${percentage}%`;
                  if (currentText !== newText) {
                    percentageEl.textContent = newText;
                  }
                }

                // Update status only if changed
                const statusEl = containerEl.querySelector(".progress-status");
                if (statusEl) {
                  const statusClass = isValid ? "valid" : "invalid";
                  const statusText = isValid
                    ? this.t("complete")
                    : this.t("incomplete");
                  const currentStatusClass = statusEl.classList.contains(
                    "valid",
                  )
                    ? "valid"
                    : "invalid";

                  if (currentStatusClass !== statusClass) {
                    statusEl.className = `progress-status ${statusClass}`;
                    statusEl.innerHTML = `
                      <span class="progress-status-icon ${statusClass}"></span>
                      ${statusText}
                    `;
                  }
                }

                // Update progress details if weight/volume changed OR if invalid container state changed
                const detailsEl = containerEl.querySelector(".type1-details");
                const currentInvalidMessage = detailsEl
                  ?.querySelector(".manufacturer-validation-message.error")
                  ?.textContent.includes(
                    this.t("invalid_container_weight_max_volume_min"),
                  );
                const invalidStateChanged =
                  (container.isInvalidContainer || false) !==
                  (currentInvalidMessage || false);

                // Check if weight changed by comparing displayed weight with new weight
                const weightInfoEl = detailsEl?.querySelector(".weight-info");
                let weightChanged = false;
                if (weightInfoEl) {
                  const currentWeightText = weightInfoEl.textContent;
                  const newWeightText = `${this.t("weight")}: ${container.totalWeight.toFixed(0)} kg / ${container.maxWeight.toFixed(0)} kg (${Math.round(container.weightPercent)}%)`;
                  weightChanged = currentWeightText !== newWeightText;
                }

                if (
                  detailsEl &&
                  (changedLineNumbers.size > 0 ||
                    progressChanged ||
                    invalidStateChanged ||
                    weightChanged)
                ) {
                  const newDetails = this.renderProgressDetails(container);
                  if (newDetails) {
                    detailsEl.outerHTML = newDetails;
                  }
                }
              }
            }
          } else {
            // Other types: Update single progress bar
            const progressEl = manufacturerGroup.querySelector(
              ".manufacturer-progress",
            );
            if (!progressEl) {
              return false;
            }

            const percentage = Math.round(progressData.percentage || 0);
            const isValid = progressData.valid || false;
            const progressClass = isValid
              ? "complete"
              : percentage >= 50
                ? "warning"
                : "error";
            const statusClass = isValid ? "valid" : "invalid";
            const statusText = isValid
              ? this.t("complete")
              : this.t("incomplete");

            // Update progress bar width only if changed
            const progressBarFill =
              progressEl.querySelector(".progress-bar-fill");
            if (progressBarFill) {
              const currentWidth = parseFloat(progressBarFill.style.width) || 0;
              const newWidth = percentage;

              if (Math.abs(currentWidth - newWidth) > 0.5) {
                progressBarFill.className = `progress-bar-fill ${progressClass}`;
                requestAnimationFrame(() => {
                  progressBarFill.style.width = `${percentage}%`;
                });
              }
            }

            // Update percentage only if changed
            const percentageEl = progressEl.querySelector(
              ".progress-percentage",
            );
            if (percentageEl) {
              const currentText = percentageEl.textContent.trim();
              const newText = `${percentage}%`;
              if (currentText !== newText) {
                percentageEl.textContent = newText;
              }
            }

            // Update status only if changed
            const statusEl = progressEl.querySelector(".progress-status");
            if (statusEl) {
              const currentStatusClass = statusEl.classList.contains("valid")
                ? "valid"
                : "invalid";
              if (currentStatusClass !== statusClass) {
                statusEl.className = `progress-status ${statusClass}`;
                statusEl.innerHTML = `
                  <span class="progress-status-icon ${statusClass}"></span>
                  ${statusText}
                `;
              }
            }

            // Update progress details only if changed
            const detailsContainer = progressEl.querySelector(
              ".type1-details, .type2-details, .type3-details, .type4-details",
            );
            if (
              detailsContainer &&
              (changedLineNumbers.size > 0 || progressChanged)
            ) {
              const newDetails = this.renderProgressDetails(progressData);
              if (newDetails) {
                detailsContainer.outerHTML = newDetails;
              }
            }
          }
        }

        // Update cart items for this manufacturer
        const itemsTable = manufacturerGroup.querySelector("table tbody");
        if (itemsTable) {
          // Only update items that actually changed
          for (const item of group.items) {
            const lineNumber =
              item.line || (item.index !== undefined ? item.index + 1 : 1);

            // Skip if this item didn't change
            if (
              changedLineNumbers.size > 0 &&
              !changedLineNumbers.has(lineNumber)
            ) {
              continue;
            }

            const row = itemsTable.querySelector(`#CartItem-${lineNumber}`);

            if (row) {
              // Update quantity only if it changed
              const quantityInput = row.querySelector('input[type="number"]');
              if (quantityInput) {
                const currentValue = parseInt(quantityInput.value) || 0;
                if (currentValue !== item.quantity) {
                  quantityInput.value = item.quantity;
                }
              }

              // Update price only if it changed
              const priceEl = row.querySelector(".price ins, .price");
              if (priceEl) {
                const linePrice = item.final_line_price || item.line_price || 0;
                const originalLinePrice = item.original_line_price || linePrice;
                const hasDiscount = originalLinePrice !== linePrice;

                // Check if price actually changed by comparing text content
                const currentPriceText = priceEl.textContent.trim();
                const newPriceText = this.formatMoney(linePrice);
                if (
                  currentPriceText !== newPriceText &&
                  !currentPriceText.includes(newPriceText)
                ) {
                  if (hasDiscount) {
                    priceEl.innerHTML = `
                      <del>${this.formatMoney(originalLinePrice)}</del>
                      <ins>${this.formatMoney(linePrice)}</ins>
                    `;
                  } else {
                    priceEl.innerHTML = `<ins>${this.formatMoney(linePrice)}</ins>`;
                  }
                }
              }

              // Update product progress bar for Type 3/4
              if (progressData.type === 3 || progressData.type === 4) {
                const productProgressMap = {};
                if (progressData.type === 3 && progressData.productProgress) {
                  for (const product of progressData.productProgress) {
                    productProgressMap[product.productId] = product;
                  }
                } else if (
                  progressData.type === 4 &&
                  progressData.unitsProgress &&
                  progressData.unitsProgress.productProgress
                ) {
                  for (const product of progressData.unitsProgress
                    .productProgress) {
                    productProgressMap[product.productId] = product;
                  }
                }

                const productProgress = productProgressMap[item.product_id];
                const productProgressEl = row.querySelector(
                  ".product-row-progress",
                );

                if (productProgress && productProgress.minUnits > 0) {
                  const productPercentage = Math.round(
                    productProgress.percentage || 0,
                  );
                  const productProgressClass = productProgress.valid
                    ? "complete"
                    : productPercentage >= 50
                      ? "warning"
                      : "error";
                  const productStatusClass = productProgress.valid
                    ? "valid"
                    : "invalid";
                  const productStatusText = productProgress.valid
                    ? this.t("complete")
                    : this.t("incomplete");

                  if (productProgressEl) {
                    // Update existing progress bar
                    const fill = productProgressEl.querySelector(
                      ".product-progress-bar-fill",
                    );
                    if (fill) {
                      fill.className = `product-progress-bar-fill ${productProgressClass}`;
                      requestAnimationFrame(() => {
                        fill.style.width = `${productPercentage}%`;
                      });
                    }

                    const textEl = productProgressEl.querySelector(
                      ".product-progress-text",
                    );
                    if (textEl) {
                      textEl.textContent = `${productProgress.quantity} / ${productProgress.minUnits} ${this.t("units")}`;
                    }

                    const percentEl = productProgressEl.querySelector(
                      ".product-progress-percentage",
                    );
                    if (percentEl) {
                      percentEl.textContent = `${productPercentage}%`;
                    }

                    // Update status text and class
                    const statusEl = productProgressEl.querySelector(
                      ".product-progress-status",
                    );
                    if (statusEl) {
                      statusEl.className = `product-progress-status ${productStatusClass}`;
                      const statusIcon = statusEl.querySelector(
                        ".progress-status-icon",
                      );
                      if (statusIcon) {
                        statusIcon.className = `progress-status-icon ${productStatusClass}`;
                      }
                      // Update text content - find text node after icon
                      const icon = statusEl.querySelector(
                        ".progress-status-icon",
                      );
                      if (icon) {
                        // Remove all text nodes and add new one
                        Array.from(statusEl.childNodes).forEach((node) => {
                          if (node.nodeType === Node.TEXT_NODE) {
                            node.remove();
                          }
                        });
                        // Add text after icon
                        icon.insertAdjacentText("afterend", productStatusText);
                      }
                    }
                  } else {
                    // Need to add progress bar - do full render
                    return false;
                  }
                } else if (productProgressEl) {
                  // Remove progress bar if no longer needed
                  productProgressEl.remove();
                }
              }
            } else {
              // Item doesn't exist, need full render
              return false;
            }
          }

          // Check if items were removed - only if we detected removed items
          if (changedLineNumbers.size > 0) {
            const existingRows = itemsTable.querySelectorAll(
              "tr[data-line-number]",
            );
            const existingLineNumbers = Array.from(existingRows).map((row) =>
              parseInt(row.getAttribute("data-line-number")),
            );
            const currentLineNumbers = group.items.map(
              (item) =>
                item.line || (item.index !== undefined ? item.index + 1 : 1),
            );

            // If line numbers don't match, need full render
            if (
              existingLineNumbers.length !== currentLineNumbers.length ||
              !existingLineNumbers.every(
                (line, idx) => line === currentLineNumbers[idx],
              )
            ) {
              return false;
            }

            // Remove rows for items that no longer exist
            for (const lineNumber of changedLineNumbers) {
              if (!currentLineNumbers.includes(lineNumber)) {
                const rowToRemove = itemsTable.querySelector(
                  `#CartItem-${lineNumber}`,
                );
                if (rowToRemove) {
                  // Smoothly fade out and remove
                  rowToRemove.style.transition = "opacity 0.2s ease";
                  rowToRemove.style.opacity = "0";
                  setTimeout(() => {
                    if (rowToRemove.parentNode) {
                      rowToRemove.remove();
                    }
                  }, 200);
                }
              }
            }
          }
        }
      }

      // Update items without manufacturer
      const noManufacturerGroup = this.container.querySelector(
        '[data-manufacturer-id="none"]',
      );
      if (newNoManufacturer.length > 0) {
        if (!noManufacturerGroup) {
          return false; // Need to add the group
        }
        // Update items in no-manufacturer group (similar logic as above)
        const itemsTable = noManufacturerGroup.querySelector("table tbody");
        if (itemsTable) {
          for (const item of newNoManufacturer) {
            const lineNumber =
              item.line || (item.index !== undefined ? item.index + 1 : 1);
            const row = itemsTable.querySelector(`#CartItem-${lineNumber}`);
            if (row) {
              const quantityInput = row.querySelector('input[type="number"]');
              if (
                quantityInput &&
                parseInt(quantityInput.value) !== item.quantity
              ) {
                quantityInput.value = item.quantity;
              }

              // Update price
              const priceEl = row.querySelector(".price ins, .price");
              if (priceEl) {
                const linePrice = item.final_line_price || item.line_price || 0;
                const originalLinePrice = item.original_line_price || linePrice;
                const hasDiscount = originalLinePrice !== linePrice;

                if (hasDiscount) {
                  priceEl.innerHTML = `
                    <del>${this.formatMoney(originalLinePrice)}</del>
                    <ins>${this.formatMoney(linePrice)}</ins>
                  `;
                } else {
                  priceEl.innerHTML = `<ins>${this.formatMoney(linePrice)}</ins>`;
                }
              }
            } else {
              return false;
            }
          }
        }
      } else if (noManufacturerGroup) {
        // Remove no-manufacturer group if empty
        noManufacturerGroup.remove();
      }

      return true; // Incremental update succeeded
    } catch (error) {
      console.error("Error in incremental update:", error);
      // Re-enable controls on error
      this.setQuantityControlsEnabled(true);
      return false; // Fall back to full render
    }
  }

  setupCartInteractions() {
    if (!this.container) return;

    // Setup quantity buttons
    const quantityButtons = this.container.querySelectorAll(
      ".quantity .minus, .quantity .plus",
    );
    quantityButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const input = button.parentElement.querySelector(
          'input[type="number"]',
        );
        if (!input) return;

        // Disable all quantity controls during update
        this.setQuantityControlsEnabled(false);

        // Use data-line attribute which contains the correct line number
        const lineNumber = parseInt(
          button.dataset.line || input.dataset.line || input.dataset.index,
        );
        if (!lineNumber) {
          console.error("Could not find line number for quantity button");
          this.setQuantityControlsEnabled(true);
          return;
        }

        const currentValue = parseInt(input.value) || 1;
        const newValue = button.classList.contains("plus")
          ? currentValue + 1
          : Math.max(1, currentValue - 1);

        if (newValue !== currentValue) {
          this.updateQuantity(lineNumber, newValue);
        } else {
          this.setQuantityControlsEnabled(true);
        }
      });
    });

    // Update set-number-x-quantity when quantity input changes
    const updateSetNumberDisplay = (input) => {
      const row = input.closest("tr.cart-item-row");
      if (!row) return;
      const setNumberEl = row.querySelector(".set-number-x-quantity");
      if (!setNumberEl) return;
      const nDePiezas = parseFloat(
        setNumberEl.dataset.nDePiezasPorSet || "0",
      ) || 0;
      const qty = parseInt(input.value, 10) || 0;
      const total = nDePiezas > 0 ? nDePiezas * qty : 0;
      const label =
        typeof theme !== "undefined" &&
        theme.manufacturerCart &&
        theme.manufacturerCart.sets_totales
          ? theme.manufacturerCart.sets_totales
          : "Total sets: ";
      setNumberEl.textContent = total > 0 ? `${label}${total}` : "";
    };

    this.container.querySelectorAll(".quantity input[type='number']").forEach(
      (input) => {
        input.addEventListener("input", () => updateSetNumberDisplay(input));
        input.addEventListener("change", () => updateSetNumberDisplay(input));
      },
    );

    // Setup remove buttons
    const removeButtons = this.container.querySelectorAll(
      ".remove.custom-remove",
    );
    removeButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        // Use data-line attribute which contains the correct line number
        const lineNumber = parseInt(
          button.dataset.line || button.dataset.index,
        );
        if (!lineNumber) {
          console.error("Could not find line number for remove button");
          return;
        }
        this.updateQuantity(lineNumber, 0);
      });
    });
  }

  setQuantityControlsEnabled(enabled) {
    if (!this.container) return;

    const quantityButtons = this.container.querySelectorAll(
      '.quantity .minus, .quantity .plus, .quantity input[type="number"]',
    );
    const removeButtons = this.container.querySelectorAll(
      ".remove.custom-remove",
    );

    quantityButtons.forEach((control) => {
      if (enabled) {
        control.removeAttribute("disabled");
        control.style.pointerEvents = "";
        control.style.opacity = "";
      } else {
        control.setAttribute("disabled", "disabled");
        control.style.pointerEvents = "none";
        control.style.opacity = "0.6";
      }
    });

    removeButtons.forEach((button) => {
      if (enabled) {
        button.removeAttribute("disabled");
        button.style.pointerEvents = "";
        button.style.opacity = "";
      } else {
        button.setAttribute("disabled", "disabled");
        button.style.pointerEvents = "none";
        button.style.opacity = "0.6";
      }
    });
  }

  async updateQuantity(lineNumber, quantity) {
    // lineNumber should be 1-based (Shopify cart API uses 1-based line numbers)
    // This corresponds to the position in cart.items array (1 = first item, 2 = second item, etc.)

    // Disable controls immediately
    this.setQuantityControlsEnabled(false);

    // Skip validation for removal (quantity 0) - always allow removal
    if (quantity !== 0 && typeof ManufacturerCart !== "undefined") {
      const manufacturerCart = new ManufacturerCart();
      const validation = await manufacturerCart.validateQuantityUpdate(
        lineNumber,
        quantity,
      );

      if (!validation.valid) {
        // Show error message to user
        alert(validation.message || "Cannot update quantity");
        this.setQuantityControlsEnabled(true);
        return;
      }
    }

    // Find the cart instance
    if (!window.customCart) {
      // Try to get or create cart instance
      const cartContainer = document.getElementById("Cart");
      if (cartContainer) {
        window.customCart = new Cart();
      } else {
        console.error("Cart container not found");
        this.setQuantityControlsEnabled(true);
        return;
      }
    }

    // Use the existing Cart class to update quantity
    // Cart.updateQuantity expects 1-based line number
    window.customCart.updateQuantity(lineNumber, quantity);

    // The cart.js will trigger 'line-item:change:end' event which we listen to
    // Controls will be re-enabled in the render() method after update completes
  }

  async refreshCartData() {
    // Fetch fresh cart data with metafield information
    try {
      // Fetch the cart section to get metafield data
      const sectionResponse = await fetch(
        `${window.location.pathname}?sections=main-cart`,
      );
      const sectionData = await sectionResponse.text();
      const parsedSection = JSON.parse(sectionData);

      if (parsedSection["main-cart"]) {
        // Create a temporary container to parse the HTML
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = parsedSection["main-cart"];

        // Find and execute the script that sets window.cartItemsData
        const scripts = tempDiv.querySelectorAll("script");
        for (const script of scripts) {
          if (
            script.textContent &&
            script.textContent.includes("window.cartItemsData")
          ) {
            // Use Function constructor instead of eval for better security
            try {
              new Function(script.textContent)();
            } catch (e) {
              console.warn("Could not execute cart data script:", e);
            }
          }
        }
      }

      // Use the updated window.cartItemsData or fallback to basic cart
      if (window.cartItemsData && window.cartItemsData.length > 0) {
        this.cart = { items: window.cartItemsData };
      } else {
        const response = await fetch(window.Shopify.routes.root + "cart.js");
        const cartData = await response.json();
        this.cart = cartData;
      }

      // CRITICAL: Add line numbers based on position in cart.items array
      // Shopify cart.js doesn't include index, but cart API uses 1-based line numbers
      if (this.cart && this.cart.items) {
        this.cart.items.forEach((item, index) => {
          item.index = index; // 0-based index
          item.line = index + 1; // 1-based line number for Shopify API
        });
      }
    } catch (error) {
      console.error("Error refreshing cart data:", error);
      // Fallback to basic cart data
      try {
        const response = await fetch(window.Shopify.routes.root + "cart.js");
        const cartData = await response.json();
        this.cart = cartData;
      } catch (e) {
        console.error("Failed to fetch cart data:", e);
      }
    }
  }

  renderManufacturerGroup(
    manufacturerId,
    nombre,
    foto,
    urlFabricante,
    items,
    progressData,
  ) {
    console.log(JSON.stringify(nombre), nombre);
    const headerContent =
      (foto
        ? `<img src="${foto}" alt="${nombre}" class="manufacturer-logo">`
        : "") + `<h3 class="manufacturer-name">${nombre}</h3>`;
    const headerHtml = urlFabricante
      ? `<a href="${urlFabricante}" class="manufacturer-link">${headerContent}</a>`
      : headerContent;
    let html = `
      <div class="manufacturer-group" data-manufacturer-id="${manufacturerId}">
        <div class="manufacturer-group-header">
          ${headerHtml}
        </div>
    `;

    // Handle Type 1 with multiple containers
    if (
      progressData.type === 1 &&
      progressData.containers &&
      Array.isArray(progressData.containers)
    ) {
      // Render each container separately
      for (const container of progressData.containers) {
        const percentage = Math.round(container.percentage || 0);
        const isValid = container.valid || false;
        const isComplete = isValid && percentage >= 100;

        if (isComplete) {
          // Minimized view for completed containers
          html += `
            <div class="container-progress container-complete" data-container-index="${container.containerIndex}">
              <div class="container-header-minimal">
                <span class="container-title-minimal">${this.t("container")} ${container.containerIndex}</span>
                <span class="progress-status valid">
                  <span class="progress-status-icon valid"></span>
                  ${this.t("complete")}
                </span>
              </div>
            </div>
          `;
        } else {
          // Full view for incomplete containers
          // Progress bar color based on validity, not weight warning
          // Weight warning is informational only and doesn't affect bar color
          const progressClass = isValid
            ? "complete"
            : percentage >= 50
              ? "warning"
              : "error";
          const statusClass = isValid ? "valid" : "invalid";
          const statusText = isValid
            ? this.t("complete")
            : this.t("incomplete");

          html += `
            <div class="container-progress" data-container-index="${container.containerIndex}">
              <div class="container-header">
                <h4 class="container-title">${this.t("container")} ${container.containerIndex}</h4>
              </div>
              <div class="manufacturer-progress">
                <div class="progress-bar-container">
                  <div class="progress-bar-fill ${progressClass}" style="width: ${percentage}%"></div>
                </div>
                <div class="progress-info">
                  <span class="progress-percentage">${percentage}%</span>
                  <span class="progress-status ${statusClass}">
                    <span class="progress-status-icon ${statusClass}"></span>
                    ${statusText}
                  </span>
                </div>
                ${this.renderProgressDetails(container)}
              </div>
            </div>
          `;
        }
      }
    } else {
      // Single progress bar for other types
      const percentage = Math.round(progressData.percentage || 0);
      const isValid = progressData.valid || false;
      const progressClass = isValid
        ? "complete"
        : percentage >= 50
          ? "warning"
          : "error";
      const statusClass = isValid ? "valid" : "invalid";
      const statusText = isValid ? this.t("complete") : this.t("incomplete");

      html += `
        <div class="manufacturer-progress">
          <div class="progress-bar-container">
            <div class="progress-bar-fill ${progressClass}" style="width: ${percentage}%"></div>
          </div>
          <div class="progress-info">
            <span class="progress-percentage">${percentage}%</span>
            <span class="progress-status ${statusClass}">
              <span class="progress-status-icon ${statusClass}"></span>
              ${statusText}
            </span>
          </div>
          ${this.renderProgressDetails(progressData)}
        </div>
      `;
    }

    // Render items table
    html += `
      <form action="${theme.routes.cart_url}" method="post" id="cart-form-${manufacturerId}" novalidate>
        <table class="cart-items no-borders">
          <thead>
            <th class="product-name">${this.t("sections.cart.headings.product")}</th>
            <th class="product-quantity">${this.t("sections.cart.headings.quantity_extended")}</th>
            <th class="product-total">${this.t("sections.cart.headings.total")}</th>
          </thead>
          <tbody>
    `;

    // Pass product progress data for Type 3 and Type 4
    const productProgressMap = {};
    if (progressData.type === 3 && progressData.productProgress) {
      for (const product of progressData.productProgress) {
        productProgressMap[product.productId] = product;
      }
    } else if (
      progressData.type === 4 &&
      progressData.unitsProgress &&
      progressData.unitsProgress.productProgress
    ) {
      for (const product of progressData.unitsProgress.productProgress) {
        productProgressMap[product.productId] = product;
      }
    }

    for (const item of items) {
      const productProgress = productProgressMap[item.product_id] || null;
      html += this.renderCartItem(item, productProgress);
    }

    html += `
          </tbody>
        </table>
      </form>
    </div>
    `;

    return html;
  }

  renderNoManufacturerGroup(items) {
    let html = `
      <div class="manufacturer-group" data-manufacturer-id="none">
        <div class="manufacturer-group-header">
          <h3 class="manufacturer-name">${this.t("other_items")}</h3>
        </div>
        <form action="${theme.routes.cart_url}" method="post" id="cart-form-none" novalidate>
          <table class="cart-items no-borders">
            <thead>
              <th class="product-name">${this.t("sections.cart.headings.product")}</th>
              <th class="product-quantity">${this.t("sections.cart.headings.quantity_extended")}</th>
              <th class="product-total">${this.t("sections.cart.headings.total")}</th>
            </thead>
            <tbody>
    `;

    for (const item of items) {
      html += this.renderCartItem(item);
    }

    html += `
            </tbody>
          </table>
        </form>
      </div>
    `;

    return html;
  }

  renderCartItem(item, productProgress = null) {
    const imageUrl = item.image || "";
    const productTitle = item.product_title || item.title || "";
    const variantTitle =
      item.variant_title && item.variant_title !== "Default Title"
        ? item.variant_title
        : "";
    const quantity = item.quantity || 1;
    const nDePiezasPorSet =
      parseFloat(
        item.product?.metafields?.custom?.n_de_piezas_por_set?.value || 0,
      ) || 0;
    const totalPiezas = nDePiezasPorSet > 0 ? nDePiezasPorSet * quantity : 0;
    const linePrice = item.final_line_price || item.line_price || 0;
    const originalLinePrice = item.original_line_price || linePrice;
    const hasDiscount = originalLinePrice !== linePrice;
    const itemKey = item.key || "";

    // Use the line number that was set during render() based on cart.items array position
    // This ensures the line number matches Shopify's internal cart order
    const lineNumber =
      item.line || (item.index !== undefined ? item.index + 1 : 1);

    // Render product progress bar if available (for Type 3 and Type 4)
    let productProgressHtml = "";
    if (productProgress && productProgress.minUnits > 0) {
      const productPercentage = Math.round(productProgress.percentage || 0);
      const productProgressClass = productProgress.valid
        ? "complete"
        : productPercentage >= 50
          ? "warning"
          : "error";
      const productStatusClass = productProgress.valid ? "valid" : "invalid";
      const productStatusText = productProgress.valid
        ? this.t("complete")
        : this.t("incomplete");

      productProgressHtml = `
        <div class="product-row-progress">
          <div class="product-progress-bar-container">
            <div class="product-progress-bar-fill ${productProgressClass}" style="width: ${productPercentage}%"></div>
          </div>
          <div class="product-progress-info">
            <span class="product-progress-text">${productProgress.quantity} / ${productProgress.minUnits} ${this.t("units")}</span>
            <span class="product-progress-percentage">${productPercentage}%</span>
            <span class="product-progress-status ${productStatusClass}">
              <span class="progress-status-icon ${productStatusClass}"></span>
              ${productStatusText}
            </span>
          </div>
        </div>
      `;
    }

    return `
      <tr id="CartItem-${lineNumber}" class="cart-item-row" data-product-id="${item.product_id}" data-variant-id="${item.variant_id}" data-line-number="${lineNumber}">
        <td class="product-name">
          <div class="product-cart-item">
            <div class="product-cart-item-image">
              ${imageUrl ? `<img src="${imageUrl}" alt="${productTitle}" loading="lazy">` : ""}
            </div>
            <div>
              <a href="${item.url || "#"}" class="cart-product-link">${productTitle}</a>
              ${variantTitle ? `<div class="product-cart-item-options">${variantTitle}</div>` : ""}
              ${productProgressHtml}
              <div class="set-number-x-quantity" data-n-de-piezas-por-set="${nDePiezasPorSet}">${totalPiezas > 0 ? `${this.t("sets_totales")}${totalPiezas}` : ""}</div>
            </div>
          </div>
        </td>
        <td class="product-quantity">
          <div class="quantity buttons_added">
            <button class="minus" type="button" aria-label="${this.t("decrease_quantity")}" data-line="${lineNumber}">-</button>
            <input type="number" id="quantity_${itemKey.replace(/:/g, "_")}" 
                   class="qty custom-qty" value="${quantity}" 
                   data-line="${lineNumber}" data-index="${lineNumber}" min="1">
            <button class="plus" type="button" aria-label="${this.t("increase_quantity")}" data-line="${lineNumber}">+</button>
          </div>
        </td>
        <td class="product-subtotal">
          <div class="product-subtotal-wrapper">
            <span class="price">
              ${hasDiscount ? `<del>${this.formatMoney(originalLinePrice)}</del>` : ""}
              <ins>${this.formatMoney(linePrice)}</ins>
            </span>
            <a href="#" class="remove custom-remove" data-line="${lineNumber}" data-index="${lineNumber}" aria-label="Remove item">×</a>
          </div>
        </td>
      </tr>
    `;
  }

  renderProgressDetails(progressData) {
    if (!progressData) return "";

    let details = "";

    switch (progressData.type) {
      case 1: // Volume-based
        details = `
          <div class="type1-details">
            <div>${this.t("volume")}: ${progressData.current.toFixed(2)} m³ / ${progressData.target.toFixed(2)} m³</div>
            <div class="weight-info">${this.t("weight")}: ${progressData.totalWeight.toFixed(0)} kg / ${progressData.maxWeight.toFixed(0)} kg (${Math.round(progressData.weightPercent)}%)</div>
            ${progressData.weightWarning ? `<div class="manufacturer-validation-message weight-warning">${this.t("weight_warning_70_percent")}</div>` : ""}
            ${progressData.isInvalidContainer ? `<div class="manufacturer-validation-message error">${this.t("invalid_container_weight_max_volume_min")}</div>` : ""}
            ${!progressData.valid && !progressData.isInvalidContainer ? `<div class="manufacturer-validation-message error">${this.t("minimum_volume_requirement_not_met")}</div>` : ""}
          </div>
        `;
        break;
      case 2: // Min price
        details = `
          <div class="type2-details">
            <div>${this.t("price")}: ${this.formatMoney(progressData.current)} / ${this.formatMoney(progressData.target)}</div>
            ${!progressData.valid ? `<div class="manufacturer-validation-message error">${this.t("need_more", { amount: this.formatMoney(progressData.target - progressData.current) })}</div>` : ""}
          </div>
        `;
        break;
      case 3: // Min units per product
        // Individual progress bars are shown in product rows, not here
        details = `<div class="type3-details"><div class="type3-summary">${this.t("individual_product_progress_shown")}</div></div>`;
        break;
      case 4: // Min price + Min units
        // Individual product progress bars are shown in product rows, not here
        details = `
          <div class="type4-details">
            <div class="price-progress">
              <div>${this.t("price")}: ${this.formatMoney(progressData.priceProgress.current)} / ${this.formatMoney(progressData.priceProgress.target)}</div>
              ${!progressData.priceProgress.valid ? `<div class="manufacturer-validation-message error">${this.t("need_more", { amount: this.formatMoney(progressData.priceProgress.target - progressData.priceProgress.current) })}</div>` : ""}
            </div>
            <div class="units-progress-summary">
              <div>${this.t("individual_product_progress_shown")}</div>
            </div>
            ${!progressData.valid ? `<div class="manufacturer-validation-message error">${this.t("both_requirements_must_be_met")}</div>` : ""}
          </div>
        `;
        break;
    }

    return details;
  }

  async updateCheckoutState(progress) {
    const checkoutStatus = await this.manufacturerCart.isCheckoutAllowed(
      this.cart.items,
    );
    const checkoutButton = document.getElementById("checkout-button");
    const blockedMessage = document.getElementById("checkout-blocked-message");
    const blockedList = document.getElementById("checkout-blocked-list");

    if (!checkoutButton) return;

    if (!checkoutStatus.allowed) {
      checkoutButton.classList.add("disabled");
      checkoutButton.style.pointerEvents = "none";
      checkoutButton.href = "#";

      if (blockedMessage && blockedList) {
        blockedMessage.style.display = "block";
        blockedList.innerHTML = "";
        for (const invalid of checkoutStatus.invalidManufacturers) {
          const li = document.createElement("li");
          li.textContent = `${invalid.name}: ${this.t("requirements_not_met")}`;
          blockedList.appendChild(li);
        }
      }
    } else {
      checkoutButton.classList.remove("disabled");
      checkoutButton.style.pointerEvents = "";
      checkoutButton.href = "/checkout";

      if (blockedMessage) {
        blockedMessage.style.display = "none";
      }
    }
  }

  formatMoney(cents) {
    if (
      typeof theme !== "undefined" &&
      theme.settings &&
      theme.settings.money_with_currency_format
    ) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "EUR",
      }).format(cents / 100);
    }
    return (cents / 100).toFixed(2) + " €";
  }

  translate(key, params = {}) {
    // Handle cart headings (these are passed via window.theme.cartHeadings)
    if (key === "sections.cart.headings.product") {
      if (
        typeof theme !== "undefined" &&
        theme.cartHeadings &&
        theme.cartHeadings.product
      ) {
        return theme.cartHeadings.product;
      }
      return "Product";
    }
    if (key === "sections.cart.headings.quantity") {
      if (
        typeof theme !== "undefined" &&
        theme.cartHeadings &&
        theme.cartHeadings.quantity
      ) {
        return theme.cartHeadings.quantity;
      }
      return "Quantity";
    }
    if (key === "sections.cart.headings.quantity_extended") {
      if (
        typeof theme !== "undefined" &&
        theme.cartHeadings &&
        theme.cartHeadings.quantity_extended
      ) {
        return theme.cartHeadings.quantity_extended;
      }
      return "Pallets/boxes quantity";
    }
    if (key === "sections.cart.headings.total") {
      if (
        typeof theme !== "undefined" &&
        theme.cartHeadings &&
        theme.cartHeadings.total
      ) {
        return theme.cartHeadings.total;
      }
      return "Total";
    }

    // Access translations from theme object
    if (typeof theme !== "undefined" && theme.manufacturerCart) {
      let translation = theme.manufacturerCart[key] || "";

      // Handle variable substitution (e.g., {{ amount }})
      if (params && Object.keys(params).length > 0) {
        Object.keys(params).forEach((param) => {
          translation = translation.replace(
            new RegExp(`{{\\s*${param}\\s*}}`, "g"),
            params[param],
          );
        });
      }

      return translation || key;
    }

    // Final fallback
    return key;
  }

  t(key, params = {}) {
    return this.translate(key, params);
  }
}

// ManufacturerCartRenderer is kept for reference but initialization and event handling
// are now managed by ManufacturerCartElement (manufacturer-cart-element.js).
window.ManufacturerCartRenderer = ManufacturerCartRenderer;
